import { useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isDemoMode, setDemoMode, subscribeDemoMode } from "@/lib/demo/mode";
import { track } from "@/lib/tracking/analytics";

/** Whether this tab shows the sample company (src/lib/demo). */
export function useIsDemoMode(): boolean {
  return useSyncExternalStore(subscribeDemoMode, isDemoMode, () => false);
}

export type DemoSource = "dashboard" | "onboarding";

/**
 * Opening and leaving the sample data. Both drop the whole query cache before
 * the switch, and `DemoBoundary` remounts the pages after it, so no row of the
 * real company is shown as sample or the other way round.
 */
export function useDemoMode() {
  const demo = useIsDemoMode();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const switchTo = useCallback(
    async (on: boolean, to: string) => {
      await queryClient.cancelQueries();
      queryClient.removeQueries();
      setDemoMode(on);
      navigate(to);
    },
    [queryClient, navigate]
  );

  const open = useCallback(
    (source: DemoSource, language: string) => {
      track("demo_opened", { source });
      return switchTo(true, `/${language}/dashboard`);
    },
    [switchTo]
  );

  const exit = useCallback(
    (language: string) => {
      track("demo_exited");
      return switchTo(false, `/${language}/dashboard`);
    },
    [switchTo]
  );

  return { demo, open, exit };
}
